import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { IRule } from '@/classes/rules/IRule';
import { DefaultValue } from '@/classes/rules/DefaultValue';
import { RuleFieldMessage } from './RuleFieldMessage';
import { Invalidation } from '@/classes/rules/Invalidation';
import { FieldUsage } from '@/classes/rules/FieldUsage';

const noDefaultValue = () => null;
const isArray = (param: unknown[]) => param instanceof Array;

/**
 * Generic IRule to hanle arrays. The generic type is the one of the IRule to handle what the array contains.
 * It can take has parameter a function to validate the array object, and a sample Rule to validate each of
 * the array values.
 * Both validations (Array object validation and array values validations) are needed to validate an array.
 */
export class ArrayRule<ValueRule extends IRule<Context>, Context = ScenarioExt> implements IRule<Context> {

  public readonly key: string;
  public readonly sampleRule: ValueRule;
  public required: boolean;
  public displayed: boolean = true;
  protected errorMessages: RuleFieldMessage[] = [];
  public fieldUsage: FieldUsage;
  public isAvailable: (context: Context) => boolean;
  private getDefaultInstance: (context?: Context) => DefaultValue<any[]> | null;
  private validateArray: (arr: any[], errorMessages?: RuleFieldMessage[]) => boolean;

  public constructor(
    key: string,
    isAvailable: (context: Context) => boolean,
    sampleRule: ValueRule,
    validateArray?: (val: any[], errorMessages?: RuleFieldMessage[]) => boolean,
    getDefaultInstance?: (context?: Context) => DefaultValue<any[]> | null,
    required?: boolean
  ) {
    this.key = key;
    this.sampleRule = sampleRule;
    this.isAvailable = isAvailable;
    this.validateArray = validateArray ?? isArray;
    this.getDefaultInstance = getDefaultInstance ?? noDefaultValue;
    this.required = required ?? true;
    this.fieldUsage = new FieldUsage();
  }

  get invalidation(): Invalidation {
    return this.fieldUsage.getInvalidation();
  }

  public getErrorMessages(): RuleFieldMessage[] {
    return Array.from(this.errorMessages);
  }

  public checkValue(arr: any[] | null, context: Context): boolean | null {
    if (arr === null) {
      return null;
    }
    this.errorMessages.length = 0;
    const checkFunction = this.validateArray(arr, this.errorMessages);
    if (!checkFunction) {
      return checkFunction; // Array is not set or Array is not an array.
    }
    for (const sampleValue of arr) {
      if (!this.sampleRule.checkValue(sampleValue, context)) {

        return false; // One of Array's values is wrong
      }
    }
    return true; // All is good
  }

  public getDefaultValue(context?: Context): DefaultValue<any[]> | null {
    // We get the instance base with getDefaultInstance
    const defaultValue = this.getDefaultInstance(context);
    const instanceValue = defaultValue && defaultValue.value;
    // We check it's an ok object, if it's not we return null.
    if (!(instanceValue instanceof Array)) {
      return null;
    }
    const sampleDefault = this.sampleRule.getDefaultValue(context);
    if (sampleDefault instanceof DefaultValue) {
      instanceValue.fill(sampleDefault.value);
    }
    return defaultValue;
  }

  public withIsAvailable(isAvailable: (context: Context) => boolean): this {
    this.isAvailable = isAvailable;
    return this;
  }

  public withDefaultValue(getDefaultInstance: (context?: Context) => DefaultValue<any[]> | null): this {
    this.getDefaultInstance = getDefaultInstance;
    return this;
  }

  public withRequired(isRequired?: boolean): this { // true is default setting for this function
    this.required = isRequired !== false;
    return this;
  }

  public withCustomCheck(validateArray: (arr: any[], errorMessages?: RuleFieldMessage[]) => boolean): this {
    this.validateArray = validateArray;
    return this;
  }

  public withFieldUsage(fieldUsage: FieldUsage): this {
    this.fieldUsage = fieldUsage;
    return this;
  }
}
